import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'
import { EthereumProvider } from '@walletconnect/ethereum-provider'

interface WalletConnectV2ConnectorArguments {
  projectId: string
  chains: number[]
  rpcMap?: { [chainId: number]: string }
  showQrModal?: boolean
}

export class WalletConnectV2Connector extends AbstractConnector {
  private readonly config: WalletConnectV2ConnectorArguments
  public provider?: Awaited<ReturnType<typeof EthereumProvider.init>>

  constructor(config: WalletConnectV2ConnectorArguments) {
    super({ supportedChainIds: config.chains })
    this.config = config
  }

  public async activate(): Promise<ConnectorUpdate> {
    if (!this.provider) {
      this.provider = await EthereumProvider.init({
        projectId: this.config.projectId,
        chains: this.config.chains,
        rpcMap: this.config.rpcMap,
        showQrModal: this.config.showQrModal ?? true,
        optionalChains: [1, 3, 4, 5, 42, 1337],
        methods: [
          'eth_sendTransaction',
          'eth_signTransaction',
          'eth_sign',
          'personal_sign',
          'eth_signTypedData',
        ],
        events: ['chainChanged', 'accountsChanged'],
      })

      this.provider.on('disconnect', this.handleDisconnect)
      this.provider.on('accountsChanged', this.handleAccountsChanged)
      this.provider.on('chainChanged', this.handleChainChanged)
    }

    const accounts = await this.provider.enable()
    const account = accounts[0]
    const chainId = this.provider.chainId

    return { provider: this.provider, account, chainId }
  }

  public async getProvider(): Promise<any> {
    return this.provider
  }

  public async getChainId(): Promise<number> {
    return this.provider?.chainId ?? 1
  }

  public async getAccount(): Promise<string | null> {
    if (!this.provider) return null
    const accounts = this.provider.accounts
    return accounts[0] ?? null
  }

  public deactivate(): void {
    if (this.provider) {
      this.provider.removeListener('disconnect', this.handleDisconnect)
      this.provider.removeListener('accountsChanged', this.handleAccountsChanged)
      this.provider.removeListener('chainChanged', this.handleChainChanged)
      this.provider.disconnect()
      this.provider = undefined
    }
  }

  public async close(): Promise<void> {
    this.deactivate()
  }

  private handleDisconnect = (): void => {
    this.emitDeactivate()
  }

  private handleAccountsChanged = (accounts: string[]): void => {
    if (accounts.length === 0) {
      this.emitDeactivate()
    } else {
      this.emitUpdate({ account: accounts[0] })
    }
  }

  private handleChainChanged = (chainId: number | string): void => {
    const chainIdNumber = typeof chainId === 'string' ? parseInt(chainId, 16) : chainId
    this.emitUpdate({ chainId: chainIdNumber })
  }
}
